import React, { useCallback, useEffect, useState } from 'react';
import SaveDocButton from './SaveDocButton';
import { Loader2, RefreshCw, Printer, Paintbrush, AlertTriangle, CheckCircle2, Upload, Layers, FileText, Lock, Unlock, Percent } from 'lucide-react';
import { CalcShadowProvider, CalcShadowToggle, CalcShadow } from '@/components/CalcShadow';
import RestorationConcreteRepairs from '@/components/RestorationConcreteRepairs';
import RestorationIronRepairs, { isIronSubtype } from '@/components/RestorationIronRepairs';
import RestorationTinTileRepairs, { isTinTileSubtype } from '@/components/RestorationTinTileRepairs';
import CompareQuotesDialog from '@/components/CompareQuotesDialog';
import BuilderRoofDetailsPanel from '@/components/BuilderRoofDetailsPanel';
import { ConfidenceBanner } from '@/components/ConfidenceBanner';
import { RoofM2ConfidenceBanner } from '@/components/RoofM2ConfidenceBanner';
import { resolveEffectiveRoofSubtype } from '@/lib/roofSubtype';

/**
 * RestorationBuilderTab — Phase 3 of Roof Service Options.
 *
 * Two-stage workflow:
 *   Stage 1 = Repair lines (defect counts × Defect Catalogue) +
 *             subtype-specific add-ons (Iron rescrew $/m² ·
 *             Concrete pointing $/lm · Tin-tile panel beat $/each)
 *   Stage 2 = Paint System (every non-optional coat × roof_m²)
 */
export default function RestorationBuilderTab({ api, appraisalId, appraisal, onRefresh, onJumpToPin, onOpenAnnotator }) {
  const [bundle, setBundle] = useState(null);
  const [paintSystems, setPaintSystems] = useState([]);
  const [dims, setDims] = useState(null);
  const [loading, setLoading] = useState(true);
  const [pushing, setPushing] = useState(false);
  const [saving, setSaving] = useState(false);
  const [flash, setFlash] = useState(null);
  const [showCompare, setShowCompare] = useState(false);
  // Iter158 Phase B — Restoration Combiner: per-appraisal discount
  // mode + percentages, plus a snapshot lock for freezing the quote
  // at a point in time while the underlying data keeps evolving.
  const [discount, setDiscount] = useState({
    mode: '', flat: 0, repairs: 0, paint: 0,
  });
  const [snapshot, setSnapshot] = useState(null); // {present, snapshot} from /snapshot endpoint
  const [form, setForm] = useState({
    restoration_paint_system_id: '',
    restoration_roof_m2:         0,
    restoration_rescrew_m2:      0,
    restoration_pointing_lm:     0,
    restoration_panel_beat_qty:  0,
  });

  const subtype = resolveEffectiveRoofSubtype(appraisal, bundle?.meta);

  // Batch T Phase 3 (2026-02) — Quick-tick External Painting Extras.
  // Loads the shared Iter219 catalogue joined with the appraisal's
  // saved picks (`restoration_extra_painting_items`). Save PUT reuses
  // the existing painting-builder endpoint so a tick here shows on the
  // Painting tab too. Push writes rows tagged
  // `component_type='External Painting Extra'` +
  // `import_source='external_painting_extras'`.
  const [extras, setExtras] = useState({ rows: [], extras_total: 0 });
  const [extrasSaving, setExtrasSaving] = useState(false);
  const [extrasPushing, setExtrasPushing] = useState(false);
  const loadExtras = useCallback(async () => {
    try {
      const { data } = await api.get(`/restoration/appraisals/${appraisalId}/external-painting-extras`);
      setExtras(data || { rows: [], extras_total: 0 });
    } catch { /* silent — section just won't render */ }
  }, [api, appraisalId]);
  useEffect(() => { loadExtras(); }, [loadExtras]);

  const setExtraQty = (item_id, qty) => {
    setExtras(prev => ({
      ...prev,
      rows: prev.rows.map(r => r.item_id === item_id
        ? { ...r, qty, line_total: Math.round(Number(qty || 0) * Number(r.sell || 0) * 100) / 100 }
        : r),
    }));
  };

  const saveExtras = async () => {
    setExtrasSaving(true);
    try {
      const items = extras.rows
        .filter(r => Number(r.qty || 0) > 0)
        .map(r => ({ item_id: r.item_id, qty: Number(r.qty) }));
      await api.put(`/painting/appraisals/${appraisalId}/extras`, { items });
      await loadExtras();
      setFlash({ tone: 'ok', msg: `Saved ${items.length} external painting extra${items.length === 1 ? '' : 's'}.` });
    } catch (e) {
      setFlash({ tone: 'err', msg: 'Save failed: ' + (e?.response?.data?.detail || e.message) });
    } finally { setExtrasSaving(false); }
  };

  const pushExtras = async () => {
    setExtrasPushing(true);
    try {
      // Save first so the push reads the latest picks.
      await saveExtras();
      const { data } = await api.post(`/restoration/appraisals/${appraisalId}/external-painting-extras/push`);
      setFlash({ tone: 'ok', msg: `Pushed ${data.inserted} external painting extra${data.inserted === 1 ? '' : 's'} (total $${(data.extras_total || 0).toFixed(2)}).` });
      onRefresh && onRefresh();
    } catch (e) {
      setFlash({ tone: 'err', msg: 'Push failed: ' + (e?.response?.data?.detail || e.message) });
    } finally { setExtrasPushing(false); }
  };


  const load = useCallback(async () => {
    setLoading(true);
    try {
      const subQuery = subtype ? `?roof_subtype=${encodeURIComponent(subtype)}` : '';
      const [preview, ps, dimsRes, snapRes] = await Promise.all([
        api.get(`/restoration/appraisals/${appraisalId}/preview`),
        api.get(`/paint-systems${subQuery}`).catch(() => ({ data: [] })),
        api.get(`/appraisals/${appraisalId}/reroof-builder/dims`).catch(() => ({ data: null })),
        api.get(`/restoration/appraisals/${appraisalId}/snapshot`).catch(() => ({ data: { present: false } })),
      ]);
      setBundle(preview.data);
      setPaintSystems(ps.data || []);
      setDims(dimsRes.data || null);
      setSnapshot(snapRes.data || { present: false });
      // Seed form with current appraisal values
      setForm({
        restoration_paint_system_id: appraisal?.restoration_paint_system_id || preview.data?.meta?.paint_system_id || '',
        restoration_roof_m2:         appraisal?.restoration_roof_m2 || preview.data?.summary?.roof_m2 || 0,
        restoration_rescrew_m2:      appraisal?.restoration_rescrew_m2 || 0,
        restoration_pointing_lm:     appraisal?.restoration_pointing_lm || 0,
        restoration_panel_beat_qty:  appraisal?.restoration_panel_beat_qty || 0,
      });
      // Seed discount state from the appraisal (preview.meta echoes
      // the persisted values back so we don't need a separate fetch).
      const m = preview.data?.meta || {};
      setDiscount({
        mode:    m.discount_mode || '',
        flat:    Number(m.discount_pct_flat || 0),
        repairs: Number(m.discount_pct_repairs || 0),
        paint:   Number(m.discount_pct_paint || 0),
      });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setLoading(false);
    }
  // iter313c H4-twin fix — depend on the specific `appraisal.*` fields
  // the form seeds from. Was `[api, appraisalId, subtype]` with
  // `appraisal` intentionally omitted → stale-closure save-revert
  // identical to the RepairsBuilderTab twin.
  }, [api, appraisalId, subtype, appraisal?.restoration_paint_system_id, appraisal?.restoration_roof_m2, appraisal?.restoration_rescrew_m2, appraisal?.restoration_pointing_lm, appraisal?.restoration_panel_beat_qty]);

  useEffect(() => { load(); }, [load]);

  const saveForm = async () => {
    setSaving(true);
    try {
      // Wave 2 H9 fix (2026-02) — TRUE partial PUT: only the fields
      // this builder owns. Backend's `exclude_unset=True` preserves
      // every other field. Previous `...appraisal` spread caused
      // subtype-revert races and stale supplier-code writes.
      await api.put(`/appraisals/${appraisalId}`, {
        restoration_paint_system_id: form.restoration_paint_system_id || '',
        restoration_roof_m2:         Number(form.restoration_roof_m2) || 0,
        restoration_rescrew_m2:      Number(form.restoration_rescrew_m2) || 0,
        restoration_pointing_lm:     Number(form.restoration_pointing_lm) || 0,
        restoration_panel_beat_qty:  parseInt(form.restoration_panel_beat_qty, 10) || 0,
      });
      if (onRefresh) await onRefresh();
      // iter313c H4-twin fix — removed `await load()`. See RepairsBuilderTab.
      setFlash({ tone: 'ok', msg: 'Restoration config saved.' });
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setSaving(false);
    }
  };

  const onPush = async () => {
    if (!window.confirm('Push these restoration lines onto the Components tab? Existing restoration-builder lines will be replaced.')) return;
    setPushing(true);
    setFlash(null);
    try {
      const { data } = await api.post(`/restoration/appraisals/${appraisalId}/push`);
      setFlash({ tone: 'ok', msg: `Pushed ${data.inserted} restoration line${data.inserted === 1 ? '' : 's'} (grand total $${data.summary.grand_total.toFixed(2)})` });
      if (onRefresh) onRefresh();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    } finally {
      setPushing(false);
    }
  };

  const onPdf = () => {
    window.open(`${api.defaults.baseURL}/restoration/appraisals/${appraisalId}/quote.pdf`, '_blank');
  };

  // Iter158 Phase B — Discount controls
  const saveDiscount = async (patch) => {
    const merged = { ...discount, ...patch };
    setDiscount(merged);
    try {
      await api.patch(`/restoration/appraisals/${appraisalId}/discount`, {
        discount_mode: merged.mode,
        discount_pct_flat: Number(merged.flat) || 0,
        discount_pct_repairs: Number(merged.repairs) || 0,
        discount_pct_paint: Number(merged.paint) || 0,
      });
      await load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  // Iter158 Phase B — Snapshot lock / unlock
  const takeSnapshot = async () => {
    if (!window.confirm('Lock the current Restoration totals as a snapshot? Quote PDFs sent from this point can reference the snapshot — the live preview keeps updating but the snapshot stays frozen until you clear or re-take it.')) return;
    try {
      const { data } = await api.post(`/restoration/appraisals/${appraisalId}/snapshot`);
      setFlash({ tone: 'ok', msg: `Snapshot locked at ${new Date(data.taken_at).toLocaleString()} — grand total $${data.summary.grand_total.toFixed(2)}` });
      await load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };
  const clearSnapshot = async () => {
    if (!window.confirm('Clear the locked snapshot? The Combiner returns to fully live mode.')) return;
    try {
      await api.delete(`/restoration/appraisals/${appraisalId}/snapshot`);
      setFlash({ tone: 'ok', msg: 'Snapshot cleared — Combiner is fully live.' });
      await load();
    } catch (e) {
      setFlash({ tone: 'error', msg: e?.response?.data?.detail || e.message });
    }
  };

  const err = bundle?.error;
  const addons = bundle?.meta?.available_addons || {};
  const s = bundle?.summary || {};

  // Determine which add-on input(s) to show based on the available_addons map.
  const showRescrew = addons.rescrew_per_m2 != null;
  const showPointing = addons.pointing_per_lm != null;
  const showPanelBeat = addons.panel_beat_per_each != null;

  return (
    <CalcShadowProvider defaultShow={true}>
    <div className="space-y-4" data-testid="restoration-builder-tab">
      <div className="flex justify-end">
        <SaveDocButton
          appraisalId={appraisalId}
          docKind="quotation_detailed"
          label="Save Quote PDF"
          expandChildren={false}
          variant="outline"
          size="sm"
          testId="restoration-save-quote-pdf"
        />
      </div>
      {/* Header — title with inline subtype switcher and dynamic
          builder-type label ("Concrete Restoration", etc.). The
          full inline dropdown row (Service Option, Primary Material,
          Paint System, Tile overrides) lives inside the Concrete
          Repairs sub-section so non-concrete subtypes don't see
          irrelevant fields. */}
      <div className="bg-gradient-to-br from-indigo-50 to-violet-50 border border-indigo-200 rounded-md p-4 flex items-start justify-between gap-3">
        <div className="flex-1">
          <h3 className="text-lg font-bold text-indigo-900 flex items-center gap-2 font-[Manrope]">
            <Paintbrush size={18} /> {(subtype ? subtypeBuilderLabel(subtype) : 'Restoration')} Quote Builder
          </h3>
          <p className="text-xs text-indigo-800/80 mt-1 max-w-2xl">
            Two-stage workflow for{' '}
            {/* Iter211 — explicit "Linked from Roof Details · {subtype}"
                chip so the operator can see WHERE this value comes from
                AND jump to the Roof Details panel to edit it. Same UX
                as the Repairs tab. */}
            {subtype ? (
              <button
                type="button"
                data-testid="restoration-roof-subtype-chip"
                onClick={() => {
                  const panel = document.querySelector('[data-testid="builder-roof-details-panel"]');
                  if (panel) {
                    panel.scrollIntoView({ behavior: 'smooth', block: 'start' });
                    panel.classList.add('ring-2', 'ring-indigo-400');
                    setTimeout(() => panel.classList.remove('ring-2', 'ring-indigo-400'), 1800);
                  }
                }}
                className="inline-flex items-center gap-1 px-1.5 py-0.5 rounded ring-1 ring-indigo-300 bg-white/70 hover:bg-indigo-100 text-indigo-900 text-[11px] font-bold align-middle"
                title="Linked from Roof Details — click to jump down and edit the Roof Type"
              >
                🔗 {subtype}
              </button>
            ) : <strong>—</strong>}{' '}
            roofs: Stage 1 captures defect repairs + subtype-specific
            add-ons; Stage 2 applies a paint system from the seeded catalogue. Configure below then push to Components.
          </p>
        </div>
        <button onClick={load} data-testid="restoration-refresh-btn" className="text-sm border border-indigo-300 bg-white text-indigo-800 rounded-md px-3 py-1.5 hover:bg-indigo-100 flex items-center gap-1 self-start">
          <RefreshCw size={13} /> Refresh
        </button>
        <button onClick={() => setShowCompare(true)} data-testid="restoration-compare-btn"
                className="text-sm border border-emerald-300 bg-emerald-50 text-emerald-800 rounded-md px-3 py-1.5 hover:bg-emerald-100 flex items-center gap-1 self-start">
          <FileText size={13} /> Compare Quotes
        </button>
      </div>

      {/* Iter167 — Roof Details quick-edit panel under the Restoration banner. */}
      <BuilderRoofDetailsPanel api={api} appraisalId={appraisalId} onApplied={load} />

      <CompareQuotesDialog api={api} appraisalId={appraisalId}
                            open={showCompare} onOpenChange={setShowCompare} />

      {flash && (
        <div data-testid="restoration-flash" className={`text-sm rounded-md p-3 border ${flash.tone === 'ok' ? 'bg-emerald-50 border-emerald-200 text-emerald-800' : 'bg-rose-50 border-rose-200 text-rose-800'}`}>
          {flash.tone === 'ok' ? <CheckCircle2 size={14} className="inline mr-1" /> : <AlertTriangle size={14} className="inline mr-1" />}
          {flash.msg}
        </div>
      )}

      {loading && !bundle ? (
        <div className="flex items-center gap-2 text-sm text-slate-500 p-6">
          <Loader2 size={14} className="animate-spin" /> Loading restoration preview…
        </div>
      ) : (
        <>
        {/* Live Dimensions chip card — always shown when diagram pins
            exist, even when the restoration quote can't yet build
            (because the operator still gets value from seeing the
            face/perimeter totals while picking a roof subtype). */}
        {dims && <RestorationDimsCard dims={dims} bundle={bundle} form={form} onRefresh={load} />}
        {err ? (
        <div data-testid="restoration-error" className="bg-rose-50 border border-rose-200 rounded-md p-4 text-sm text-rose-800">
          <div className="font-semibold flex items-center gap-2"><AlertTriangle size={14} /> Cannot build restoration quote</div>
          <div className="mt-1">{bundle.message}</div>
        </div>
      ) : (
        <>
          {/* ─── PRIMARY UI: Multi-scenario Concrete Repairs builder
              (Re-roof Builder–style sidebar + main canvas). Only
              renders for Concrete-family subtypes — non-concrete
              subtypes fall through to the legacy Stage-1-addons/Stage-2
              config form below. */}
          <RestorationConcreteRepairs
            api={api}
            appraisalId={appraisalId}
            subtype={subtype}
            onLinesChanged={load}
            onJumpToPin={onJumpToPin}
            onOpenAnnotator={onOpenAnnotator}
          />

          {/* ─── Iron (Long-Run) Repairs builder. Renders only when
              the roof subtype is Iron / Long-Run; otherwise short-
              circuits. Single-quote mode (no scenarios) for now. */}
          {isIronSubtype(subtype) && (
            <RestorationIronRepairs
              api={api}
              appraisalId={appraisalId}
              subtype={subtype}
              onLinesChanged={load}
              onJumpToPin={onJumpToPin}
              onOpenAnnotator={onOpenAnnotator}
            />
          )}

          {/* ─── Tin Tile Repairs builder (Decramastic / Colortile).
              Single-quote mode. Stage 2 paint reuses the canonical
              Decramastic / Colortile paint systems. */}
          {isTinTileSubtype(subtype) && (
            <RestorationTinTileRepairs
              api={api}
              appraisalId={appraisalId}
              subtype={subtype}
              onLinesChanged={load}
              onJumpToPin={onJumpToPin}
              onOpenAnnotator={onOpenAnnotator}
            />
          )}

          {/* Batch T Phase 3 (2026-02) · External Painting Extras.
              Quick-tick section for common paint add-ons (fascia, barge,
              downpipes, garage door, chimney, etc.). Reuses the Iter219
              `extra_painting_items` catalogue + shared per-appraisal
              storage so ticks stay in sync with the Painting tab.
              Each pushed line lands tagged `component_type='External
              Painting Extra'` so the Quote Package can line-item them
              separately from the roof restoration lines. */}
          <ExternalPaintingExtrasSection
            rows={extras.rows}
            total={extras.extras_total}
            onQtyChange={setExtraQty}
            onSave={saveExtras}
            onPush={pushExtras}
            saving={extrasSaving}
            pushing={extrasPushing}
          />

          {/* Headline summary + final Push + Restoration PDF — the
              row that turns the chosen scenario's lines into the
              actual quote and ships it to the Components tab. */}

          {/* Iter158 Phase B — Restoration Combiner card: discount mode
              + snapshot lock. Sits right above the grand summary so
              the operator can adjust the discount and see the totals
              recompute in place. */}
          <div className="bg-white border border-indigo-200 rounded-md p-3 space-y-3" data-testid="restoration-combiner-card">
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <div className="flex items-center gap-2">
                <Percent size={16} className="text-indigo-600" />
                <span className="text-sm font-bold text-indigo-900 font-[Manrope]">Combiner — Discount &amp; Snapshot</span>
              </div>
              {snapshot?.present ? (
                <div className="flex items-center gap-2 text-xs text-amber-800 bg-amber-50 border border-amber-200 px-2.5 py-1 rounded">
                  <Lock size={12} /> Snapshot locked at <strong>{new Date(snapshot.snapshot?.taken_at).toLocaleString()}</strong>
                  <button
                    data-testid="restoration-snapshot-clear-btn"
                    onClick={clearSnapshot}
                    className="ml-2 text-amber-700 hover:text-amber-900 underline inline-flex items-center gap-1"
                    title="Unlock the snapshot — go fully live"
                  >
                    <Unlock size={12} /> Clear
                  </button>
                </div>
              ) : (
                <button
                  data-testid="restoration-snapshot-lock-btn"
                  onClick={takeSnapshot}
                  className="text-xs text-indigo-700 bg-indigo-50 border border-indigo-200 hover:bg-indigo-100 px-2.5 py-1 rounded inline-flex items-center gap-1"
                  title="Freeze the current totals as a snapshot — useful when emailing a quote that should stay stable while you keep tweaking the appraisal"
                >
                  <Lock size={12} /> Lock as snapshot
                </button>
              )}
            </div>

            <div className="grid grid-cols-1 md:grid-cols-4 gap-3 text-sm">
              <label className="block">
                <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Discount mode</span>
                <select
                  data-testid="restoration-discount-mode"
                  value={discount.mode}
                  onChange={(e) => saveDiscount({ mode: e.target.value })}
                  className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded bg-white"
                >
                  <option value="">No discount</option>
                  <option value="flat">Flat % off combined</option>
                  <option value="tiered">Tiered (per stage)</option>
                </select>
              </label>
              {discount.mode === 'flat' && (
                <label className="block">
                  <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">Flat % off combined</span>
                  <input
                    type="number" min="0" max="100" step="0.1"
                    data-testid="restoration-discount-flat-pct"
                    value={discount.flat}
                    onChange={(e) => setDiscount(d => ({ ...d, flat: e.target.value }))}
                    onBlur={() => saveDiscount({})}
                    className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded"
                  />
                </label>
              )}
              {discount.mode === 'tiered' && (
                <>
                  <label className="block">
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">% off Stage-1 (Repairs)</span>
                    <input
                      type="number" min="0" max="100" step="0.1"
                      data-testid="restoration-discount-repairs-pct"
                      value={discount.repairs}
                      onChange={(e) => setDiscount(d => ({ ...d, repairs: e.target.value }))}
                      onBlur={() => saveDiscount({})}
                      className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded"
                    />
                  </label>
                  <label className="block">
                    <span className="text-[10px] text-slate-500 font-bold uppercase tracking-wider">% off Stage-2 (Paint)</span>
                    <input
                      type="number" min="0" max="100" step="0.1"
                      data-testid="restoration-discount-paint-pct"
                      value={discount.paint}
                      onChange={(e) => setDiscount(d => ({ ...d, paint: e.target.value }))}
                      onBlur={() => saveDiscount({})}
                      className="w-full mt-0.5 px-2 py-1.5 border border-slate-300 rounded"
                    />
                  </label>
                </>
              )}
              <div className="text-xs text-slate-600 self-end pb-1.5">
                {s.discount_amount > 0 ? (
                  <span data-testid="restoration-discount-applied" className="text-emerald-700 font-semibold">−${(s.discount_amount || 0).toFixed(2)} off subtotal ${(s.subtotal || 0).toFixed(2)}</span>
                ) : (
                  <span className="text-slate-400 italic">No discount applied yet</span>
                )}
              </div>
            </div>
          </div>

          <div className="grid grid-cols-2 md:grid-cols-4 gap-3" data-testid="restoration-summary">
            <SummaryCard label="Stage 1 repairs" value={`$${(s.stage1_repair_total || 0).toFixed(2)}`} tone="sky" />
            <SummaryCard label="Stage 1 add-ons" value={`$${(s.stage1_addons_total || 0).toFixed(2)}`} tone="amber" />
            <SummaryCard label="Stage 2 paint" value={`$${(s.stage2_paint_total || 0).toFixed(2)}`} tone="emerald" />
            <SummaryCard label="Grand total" value={`$${(s.grand_total || 0).toFixed(2)}`} tone="emerald" big />
          </div>
          <CalcShadow testid="restoration-grand-total-calc" tone="emerald">
            Σ = ${(s.stage1_repair_total || 0).toFixed(2)} (repairs) + ${(s.stage1_addons_total || 0).toFixed(2)} (add-ons) + ${(s.stage2_paint_total || 0).toFixed(2)} (paint) {s.discount_amount > 0 ? `− $${(s.discount_amount || 0).toFixed(2)} (discount) ` : ''}= <strong>${(s.grand_total || 0).toFixed(2)}</strong>
          </CalcShadow>
          <div className="flex flex-wrap gap-2">
            <button
              data-testid="restoration-push-btn"
              onClick={onPush}
              disabled={pushing || (s.line_count === 0)}
              className="bg-indigo-600 text-white text-sm font-semibold rounded-md px-4 py-2 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
            >
              {pushing ? <Loader2 size={14} className="animate-spin" /> : <Upload size={14} />}
              {pushing ? 'Pushing…' : 'Push chosen option to Components'}
            </button>
            <button
              data-testid="restoration-pdf-btn"
              onClick={onPdf}
              className="bg-white border border-slate-300 text-slate-700 text-sm font-semibold rounded-md px-4 py-2 hover:bg-slate-50 flex items-center gap-2"
            >
              <Printer size={14} /> Restoration Quote PDF
            </button>
            <div className="ml-auto">
              <CalcShadowToggle testid="restoration-calcs-toggle" />
            </div>
          </div>

          {/* Legacy / advanced — Stage 1 add-ons (rescrew, pointing,
              panel-beat) and Stage 2 paint pick. Collapsed by default
              so the new scenario builder above is the primary UI.
              Iron / Tin-Tile subtypes use these inputs until their
              own scenario builders ship. */}
          <details className="bg-white border border-slate-200 rounded-md" data-testid="restoration-legacy-details">
            <summary className="cursor-pointer px-4 py-2.5 text-sm font-semibold text-slate-700 hover:bg-slate-50 flex items-center gap-2">
              <Layers size={14} /> Advanced — Stage 1 add-ons &amp; Stage 2 paint configuration <span className="text-xs font-normal text-slate-500">(rescrew · pointing · panel-beat · paint system pick)</span>
            </summary>
            <div className="p-4 space-y-3" data-testid="restoration-config">
            <div className="grid grid-cols-1 md:grid-cols-2 gap-3 text-sm">
              {/* Paint system picker */}
              <label className="block">
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Paint System (Stage 2)</span>
                <select
                  data-testid="restoration-paint-system-select"
                  value={form.restoration_paint_system_id}
                  onChange={(e) => setForm({ ...form, restoration_paint_system_id: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                >
                  <option value="">— Select paint system —</option>
                  {paintSystems.map((p) => (
                    <option key={p._id} value={p._id}>
                      {p.system_name} ({p.coats?.length || 0} coats)
                    </option>
                  ))}
                </select>
              </label>

              {/* Roof m² for paint */}
              <label className="block">
                <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide flex items-center gap-1.5">
                  Roof m² (for paint Stage 2)
                  {s?.roof_m2_confidence && (
                    <RoofM2ConfidenceBanner
                      api={api}
                      appraisalId={appraisalId}
                      source={s.roof_m2_source}
                      confidence={s.roof_m2_confidence}
                      label="Roof m²"
                      compact
                    />
                  )}
                </span>
                <input
                  type="number"
                  data-testid="restoration-roof-m2-input"
                  value={form.restoration_roof_m2}
                  onChange={(e) => setForm({ ...form, restoration_roof_m2: e.target.value })}
                  className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                  placeholder="0"
                />
                <span className="text-[11px] text-slate-400">If 0, will use the sum of property Roof Details m².</span>
              </label>

              {showRescrew && (
                <label className="block">
                  <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Iron rescrew m² @ ${addons.rescrew_per_m2}/m²</span>
                  <input
                    type="number"
                    data-testid="restoration-rescrew-input"
                    value={form.restoration_rescrew_m2}
                    onChange={(e) => setForm({ ...form, restoration_rescrew_m2: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    placeholder="0"
                  />
                </label>
              )}
              {showPointing && (
                <label className="block">
                  <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Ridge pointing lm @ ${addons.pointing_per_lm}/lm</span>
                  <input
                    type="number"
                    data-testid="restoration-pointing-input"
                    value={form.restoration_pointing_lm}
                    onChange={(e) => setForm({ ...form, restoration_pointing_lm: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    placeholder="0"
                  />
                </label>
              )}
              {showPanelBeat && (
                <label className="block">
                  <span className="text-xs text-slate-500 font-semibold uppercase tracking-wide">Tin-tile panel beat (qty) @ ${addons.panel_beat_per_each}/ea</span>
                  <input
                    type="number"
                    data-testid="restoration-panel-beat-input"
                    value={form.restoration_panel_beat_qty}
                    onChange={(e) => setForm({ ...form, restoration_panel_beat_qty: e.target.value })}
                    className="mt-1 w-full border border-slate-300 rounded-md px-2 py-1.5 text-sm"
                    placeholder="0"
                  />
                </label>
              )}
            </div>
            <div className="flex justify-end pt-2 border-t border-slate-100">
              <button
                data-testid="restoration-save-config-btn"
                onClick={saveForm}
                disabled={saving}
                className="bg-indigo-600 text-white text-sm font-semibold rounded-md px-4 py-1.5 hover:bg-indigo-700 disabled:opacity-50 flex items-center gap-2"
              >
                {saving ? <Loader2 size={14} className="animate-spin" /> : null}
                {saving ? 'Saving…' : 'Save & recalculate'}
              </button>
            </div>

          {/* Stage 1 lines (repair + add-ons) */}
          <LinesTable
            title="Stage 1 — Repairs + add-ons"
            lines={[...(bundle.stage1_repair_lines || []), ...(bundle.stage1_addon_lines || [])]}
            emptyMsg="No defect counts or add-on quantities set. Open the Roof Appraisal tab to enter defect counts."
            testid="restoration-stage1-table"
            tint="slate"
          />

          {/* Stage 2 lines */}
          <LinesTable
            title="Stage 2 — Paint system"
            lines={bundle.stage2_paint_lines || []}
            emptyMsg={
              bundle.meta?.paint_system_missing
                ? 'No paint system selected.'
                : (s.roof_m2 || 0) <= 0
                  ? 'Set a Roof m² above (or fill Roof Details on the property) so paint Stage 2 can compute.'
                  : 'Selected paint system has no non-optional coats.'
            }
            testid="restoration-stage2-table"
            tint="emerald"
          />
            </div>
          </details>
        </>
      )}
        </>
      )}
    </div>
    </CalcShadowProvider>
  );
}

function SummaryCard({ label, value, tone, big = false }) {
  const toneMap = {
    slate:   'bg-slate-50 border-slate-200 text-slate-800',
    sky:     'bg-sky-50 border-sky-200 text-sky-800',
    violet:  'bg-violet-50 border-violet-200 text-violet-800',
    amber:   'bg-amber-50 border-amber-200 text-amber-800',
    emerald: 'bg-emerald-50 border-emerald-300 text-emerald-900',
  };
  return (
    <div className={`rounded-md border p-3 ${toneMap[tone] || toneMap.slate}`}>
      <div className="text-[11px] uppercase tracking-wide opacity-70">{label}</div>
      <div className={`mt-1 font-bold ${big ? 'text-2xl' : 'text-lg'}`}>{value}</div>
    </div>
  );
}

function LinesTable({ title, lines, emptyMsg, testid, tint }) {
  const headerCls = tint === 'emerald' ? 'bg-emerald-900' : tint === 'violet' ? 'bg-violet-900' : 'bg-slate-900';
  return (
    <div className="bg-white border border-slate-200 rounded-md overflow-hidden" data-testid={testid}>
      <div className="px-3 py-2 border-b border-slate-100 text-sm font-semibold text-slate-700">{title}</div>
      {(!lines || lines.length === 0) ? (
        <div className="p-4 text-xs text-slate-500 italic">{emptyMsg}</div>
      ) : (
        <table className="w-full text-sm">
          <thead className={`${headerCls} text-white text-xs uppercase tracking-wide`}>
            <tr>
              <th className="text-left p-2 px-3">Type</th>
              <th className="text-left p-2 px-3">Description</th>
              <th className="text-right p-2 px-3 w-16">Qty</th>
              <th className="text-right p-2 px-3 w-16">Unit</th>
              <th className="text-right p-2 px-3 w-24">Rate</th>
              <th className="text-right p-2 px-3 w-28">Total</th>
            </tr>
          </thead>
          <tbody>
            {lines.map((li, i) => (
              <tr key={`${li.area || i}-${li.type_name}`} className="border-t border-slate-100 hover:bg-slate-50/50">
                <td className="p-2 px-3 font-semibold text-slate-800">{li.component_type}</td>
                <td className="p-2 px-3 text-slate-600 text-xs">
                  {li.type_name}
                  <CalcShadow testid={`restoration-line-calc-${li.area || i}-${(li.type_name || '').replace(/\s+/g, '-').toLowerCase()}`}>
                    {li.quantity} {li.unit} × ${(li.rate || 0).toFixed(2)} = <strong>${(li.total || 0).toFixed(2)}</strong>
                  </CalcShadow>
                </td>
                <td className="p-2 px-3 text-right">{li.quantity}</td>
                <td className="p-2 px-3 text-right text-xs text-slate-500">{li.unit}</td>
                <td className="p-2 px-3 text-right">${(li.rate || 0).toFixed(2)}</td>
                <td className="p-2 px-3 text-right font-semibold">${(li.total || 0).toFixed(2)}</td>
              </tr>
            ))}
          </tbody>
        </table>
      )}
    </div>
  );
}

// ────────────────────────────────────────────────────────────────────────
// Live Dimensions chip card for the Restoration builder. Mirrors the
// Re-roof version but with restoration-relevant fields:
//   • Face m²          (Roof m² — the painted/restored surface)
//   • Stage-1 add-ons  (rescrew m², pointing lm, panel-beat count)
//   • Paint volume     (Σ coat coverage × face m²)
//   • Snapshot freshness chip + stale-banner (same pattern as re-roof)
// Iter159: exported so the Repairs builder tab (Stage-1 mirror) can
// reuse the exact same dims card.
export function RestorationDimsCard({ dims, bundle, form, onRefresh }) {
  // Stage-1 add-on quantities the operator entered above. The card
  // surfaces them as totals next to the diagram-derived face area so
  // the operator can sanity-check that pin data + entered qty agree.
  const rescrewM2 = Number(form?.restoration_rescrew_m2 || 0);
  const pointingLm = Number(form?.restoration_pointing_lm || 0);
  const panelBeatN = Number(form?.restoration_panel_beat_qty || 0);
  // Paint volume forecast — sum each coat's coverage × roof m².
  const roofM2 = Number(form?.restoration_roof_m2 || dims?.roof_m2 || 0);
  const paintCoats = (bundle?.stage2_paint_lines || []).filter(l => l.unit === 'm²');
  const paintLitres = paintCoats.reduce((a, l) => {
    const coverageRate = Number(l.coverage_m2_per_l || 0);
    return coverageRate > 0 ? a + (Number(l.quantity || roofM2) / coverageRate) : a;
  }, 0);

  const items = [
    { k: 'Face m²',         v: dims.roof_m2,        suffix: ' m²' },
    { k: 'Perimeter',       v: dims.perimeter_m,    suffix: ' m'  },
    { k: 'Ridging',         v: (dims.ridging_total_m != null ? dims.ridging_total_m : (Number(dims.ridge_total_m||0)+Number(dims.hip_total_m||0))), suffix: ' m' },
    { k: 'Valley',          v: dims.valley_total_m, suffix: ' m'  },
    { k: 'Rescrew',         v: rescrewM2,           suffix: ' m²' },
    { k: 'Pointing',        v: pointingLm,          suffix: ' lm' },
    { k: 'Panel beat',      text: panelBeatN > 0 ? `${panelBeatN}× tiles` : '—' },
    { k: 'Paint forecast',  text: paintLitres > 0 ? `${paintLitres.toFixed(1)} L (${paintCoats.length} coat${paintCoats.length === 1 ? '' : 's'})` : '—' },
  ];

  const computedAt = dims.dims_computed_at ? new Date(dims.dims_computed_at) : null;
  const diagramAt  = dims.diagram_updated_at ? new Date(dims.diagram_updated_at) : null;
  const stale = computedAt && diagramAt && (diagramAt.getTime() - computedAt.getTime() > 1000);
  const fmt = (d) => d ? d.toLocaleString('en-NZ', { dateStyle: 'medium', timeStyle: 'short' }) : '—';

  return (
    <div
      data-testid="restoration-dims-card"
      className={`p-3 rounded ring-1 ${stale ? 'bg-amber-50 ring-amber-300' : 'bg-violet-50/40 ring-violet-200'}`}
    >
      <div className="flex items-center justify-between mb-2 flex-wrap gap-2">
        <h3 className="text-sm font-bold text-slate-700">
          Live dimensions <span className="text-[10px] uppercase tracking-wider text-slate-500 align-middle">(totals)</span> for restoration {dims.annotations_present ? '✓' : '· empty — using zeros'}
        </h3>
        <div className="flex items-center gap-2">
          <span className="text-[10px] text-slate-500" data-testid="restoration-dims-computed-at">snapshot · {fmt(computedAt)}</span>
          <button
            onClick={onRefresh}
            data-testid="restoration-dims-refresh"
            className="text-xs text-slate-500 hover:text-slate-800 inline-flex items-center gap-1"
          >
            <RefreshCw size={12} /> refresh
          </button>
        </div>
      </div>
      {stale && (
        <div data-testid="restoration-dims-stale-banner" className="mb-2 text-xs text-amber-900 bg-amber-100 border border-amber-300 rounded px-2 py-1.5">
          ⚠ Diagram was edited after this snapshot (drawing saved {fmt(diagramAt)}). Click <em>Refresh</em>.
        </div>
      )}
      <div className="grid grid-cols-2 md:grid-cols-4 lg:grid-cols-8 gap-2">
        {items.map(({ k, v, suffix, text }) => (
          <div key={k} className="bg-white rounded px-2 py-1.5 ring-1 ring-slate-200" data-testid={`restoration-dims-${k.toLowerCase().replace(/[^a-z0-9]+/g, '-')}`}>
            <div className="text-[10px] uppercase tracking-wide text-slate-500">{k}</div>
            <div className="text-sm font-bold text-[#0F172A]">
              {text != null ? text : `${(v != null && v.toFixed) ? v.toFixed(2) : 0}${suffix || ''}`}
            </div>
          </div>
        ))}
      </div>
    </div>
  );
}


// ── Subtype switcher (inline pill in the header) ──────────────────────

/**
 * Renders the friendly Builder Type label for the current subtype.
 * Concrete subtypes → "Concrete Restoration". Iron → "Iron Restoration".
 * Tin tile → "Tin Tile Restoration". Falls back to plain "Restoration".
 * Iter159: exported so the Repairs builder tab can pick a compatible
 * "Concrete Repairs" / "Iron Repairs" / "Tin Tile Repairs" label.
 */
export function subtypeBuilderLabel(subtype) {
  const s = (subtype || '').toLowerCase();
  if (s.includes('concrete')) return 'Concrete Restoration';
  if (s.includes('iron') || s.includes('long-run') || s.includes('longrun')) return 'Iron Restoration';
  if (s.includes('decramastic') || s.includes('chipped') || s === 'decra') return 'Decramastic Restoration';
  if (s.includes('colortile') || s.includes('colourtile') || s.includes('color tile') || s.includes('colour tile')) return 'Colortile Restoration';
  if (s.includes('tin tile') || s.includes('tin')) return 'Tin Tile Restoration';
  if (s.includes('clay')) return 'Clay Tile Restoration';
  return 'Restoration';
}


/**
 * Batch T Phase 3 (2026-02) · Quick-tick External Painting Extras.
 *
 * Compact card that sits inside the Recoat/Restoration builder,
 * between the repair scenarios and the Combiner. Each catalogue item
 * gets one row: name · qty input · unit · sell · line total. Setting
 * qty > 0 marks the row as picked; saving writes the picks to the
 * shared `restoration_extra_painting_items` field on the appraisal
 * (so ticks show on the Painting tab too). Push writes the resulting
 * lines to `component_lines` tagged `component_type='External
 * Painting Extra'` + `import_source='external_painting_extras'`.
 *
 * Deliberately simpler than the Painting tab's full ExtraPaintingSection
 * (no measurement expander, no add modal, no face-pin linker) so an
 * operator on a Recoat quote can flick down the list in seconds.
 */
function ExternalPaintingExtrasSection({
  rows, total, onQtyChange, onSave, onPush, saving, pushing,
}) {
  if (!rows || rows.length === 0) return null;
  const activeCount = rows.filter(r => Number(r.qty || 0) > 0).length;
  return (
    <div
      className="bg-white border border-orange-200 rounded-md p-3 space-y-3"
      data-testid="restoration-external-painting-extras"
    >
      <div className="flex items-center justify-between gap-3 flex-wrap">
        <div className="flex items-center gap-2">
          <Paintbrush size={16} className="text-orange-600" />
          <div className="text-sm font-bold text-orange-900 font-[Manrope]">
            External Painting Extras <span className="text-xs font-normal text-slate-500 ml-1">(quick-tick — sits alongside the roof lines)</span>
          </div>
        </div>
        <div className="text-xs text-slate-600">
          {activeCount > 0 ? (
            <>
              <span className="font-semibold text-orange-800">{activeCount}</span> ticked ·
              extras total <span className="font-bold text-orange-900" data-testid="ep-extras-total">${(total || 0).toFixed(2)}</span>
            </>
          ) : (
            <span className="italic">Nothing ticked yet</span>
          )}
        </div>
      </div>

      <div className="overflow-x-auto">
        <table className="w-full text-sm">
          <thead>
            <tr className="text-left text-[11px] uppercase tracking-wide text-slate-500 border-b border-slate-200">
              <th className="py-1.5 pr-2">Item</th>
              <th className="py-1.5 pr-2 w-28">Qty</th>
              <th className="py-1.5 pr-2 w-16">Unit</th>
              <th className="py-1.5 pr-2 w-24 text-right">Sell / unit</th>
              <th className="py-1.5 pr-0 w-28 text-right">Line total</th>
            </tr>
          </thead>
          <tbody>
            {rows.map((r) => {
              const ticked = Number(r.qty || 0) > 0;
              return (
                <tr
                  key={r.item_id}
                  className={`border-b border-slate-100 ${ticked ? 'bg-orange-50/40' : ''}`}
                  data-testid={`ep-extra-row-${r.item_id}`}
                >
                  <td className="py-1.5 pr-2">
                    <label className="flex items-center gap-2 cursor-pointer">
                      <input
                        type="checkbox"
                        checked={ticked}
                        onChange={(e) => onQtyChange(r.item_id, e.target.checked ? 1 : 0)}
                        className="w-4 h-4 accent-orange-600"
                        data-testid={`ep-extra-tick-${r.item_id}`}
                      />
                      <span className={ticked ? 'font-semibold text-slate-800' : 'text-slate-700'}>
                        {r.name}
                      </span>
                    </label>
                  </td>
                  <td className="py-1.5 pr-2">
                    <input
                      type="number"
                      min="0"
                      step="0.5"
                      value={r.qty || ''}
                      onChange={(e) => onQtyChange(r.item_id, e.target.value)}
                      placeholder="0"
                      className="w-24 border border-slate-300 rounded px-2 py-1 text-right"
                      data-testid={`ep-extra-qty-${r.item_id}`}
                    />
                  </td>
                  <td className="py-1.5 pr-2 text-slate-600">{r.unit}</td>
                  <td className="py-1.5 pr-2 text-right text-slate-700">${Number(r.sell || 0).toFixed(2)}</td>
                  <td className="py-1.5 pr-0 text-right font-semibold text-slate-800">
                    {ticked ? `$${Number(r.line_total || 0).toFixed(2)}` : '—'}
                  </td>
                </tr>
              );
            })}
          </tbody>
        </table>
      </div>

      <div className="flex items-center justify-end gap-2">
        <button
          onClick={onSave}
          disabled={saving || pushing}
          data-testid="ep-extras-save-btn"
          className="text-sm px-3 py-1.5 border border-orange-300 bg-white text-orange-800 rounded-md hover:bg-orange-50 disabled:opacity-50 flex items-center gap-1"
        >
          {saving ? <Loader2 size={13} className="animate-spin" /> : <RefreshCw size={13} />}
          {saving ? 'Saving…' : 'Save picks'}
        </button>
        <button
          onClick={onPush}
          disabled={saving || pushing || activeCount === 0}
          data-testid="ep-extras-push-btn"
          className="text-sm px-3 py-1.5 rounded-md bg-orange-600 text-white hover:bg-orange-700 disabled:opacity-50 flex items-center gap-1"
        >
          {pushing ? <Loader2 size={13} className="animate-spin" /> : <Upload size={13} />}
          {pushing ? 'Pushing…' : 'Push extras to Components'}
        </button>
      </div>
    </div>
  );
}
